import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { Gift, Gamepad2, Loader2, Plus, Trash2, Trophy, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

export default function WishList() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wishlist`);
      setGames(res.data?.games || []);
    } catch (e) {
      toast.error("Failed to load your wish list");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToLibrary = async (game) => {
    setBusyId(game.id);
    try {
      await axios.post(`${API}/games/add-from-master`, { masterGameId: game.id, addAsCopy: false });
      // Game is auto-removed from the wish list server-side; drop it locally too.
      setGames((prev) => prev.filter((g) => g.id !== game.id));
      toast.success(`"${game.name}" added to your library`);
    } catch (err) {
      if (err.response?.status === 409) {
        setGames((prev) => prev.filter((g) => g.id !== game.id));
        toast.error("This game is already in your library");
      } else {
        toast.error(err.response?.data?.detail || "Failed to add game to library");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (game) => {
    setBusyId(game.id);
    try {
      await axios.delete(`${API}/wishlist/${game.id}`);
      setGames((prev) => prev.filter((g) => g.id !== game.id));
      toast.success(`Removed "${game.name}" from your wish list`);
    } catch (e) {
      toast.error("Failed to remove from wish list");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="wishlist-page">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-500/20 rounded-lg">
          <Gift className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">Wish List</h1>
          <p className="text-white/50 mt-1">
            Games you want to play. Add them to your library as you get them.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
        </div>
      ) : games.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="text-center py-16">
            <Gift className="w-12 h-12 mx-auto mb-4 text-white/20" />
            <p className="text-white/60 font-medium">Your wish list is empty</p>
            <p className="text-white/40 text-sm mt-1">
              Find a game in Browse and tap "Add to Wish List" to save it here.
            </p>
            <Button asChild variant="outline" className="mt-4 border-amber-500/30 text-amber-300 hover:bg-amber-500/10">
              <Link to="/browse" data-testid="wishlist-browse-link">Browse Games</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-white/40 text-sm">{games.length} game{games.length === 1 ? "" : "s"} on your wish list</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {games.map((game) => (
              <div
                key={game.id}
                className="flex gap-3 p-3 bg-[#121212] rounded-lg border border-amber-500/20"
                data-testid={`wishlist-game-${game.id}`}
              >
                <Link to={`/browse/${game.id}`} className="w-16 h-24 flex-shrink-0 overflow-hidden rounded bg-black/50 group">
                  {game.boxArtUrl ? (
                    <img src={game.boxArtUrl} alt={game.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Gamepad2 className="w-6 h-6 text-white/20" />
                    </div>
                  )}
                </Link>
                <div className="flex-1 min-w-0 flex flex-col">
                  <Link to={`/browse/${game.id}`} className="text-white font-medium line-clamp-2 hover:text-amber-300 transition-colors">
                    {game.name}
                  </Link>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    {game.platforms?.map((p) => <PlatformBadge key={p} platform={p} size="sm" />)}
                    {(() => {
                      const primary = game.primarySystem || (game.systems && game.systems[0]);
                      return primary ? <SystemBadge key={primary} system={primary} size="sm" /> : null;
                    })()}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-white/50">
                    {(() => {
                      const yr = (game.releaseDate || "").match(/\d{4}/)?.[0];
                      return yr ? <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{yr}</span> : null;
                    })()}
                    {game.totalAchievements > 0 && (
                      <span className="flex items-center gap-1"><Trophy className="w-3 h-3" />{game.totalAchievements}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-auto pt-2">
                    <Button
                      size="sm"
                      onClick={() => handleAddToLibrary(game)}
                      disabled={busyId === game.id}
                      className="bg-purple-600 hover:bg-purple-700 h-8 flex-1"
                      data-testid={`wishlist-add-library-${game.id}`}
                    >
                      {busyId === game.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                      Add to Library
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemove(game)}
                      disabled={busyId === game.id}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                      title="Remove from wish list"
                      data-testid={`wishlist-remove-${game.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
